import React, { useRef } from 'react';
import { Message } from 'semantic-ui-react';
import { RELATIONSHIPS_OPTIONS } from '../../constants/options';
import { FieldType, IInvitation, IInvitationError, IUser } from 'types';
import { validateEmail } from 'helpers/validation';
import styles from './createInvitation.scss';
import { isEqualCaseInsensitive } from 'helpers';
import QuashedForm from 'components/quashed-form';

export interface IProps {
  familyId: string;
  error: IInvitationError;
  loading: boolean;
  user: IUser;
  onSubmit: (invitation: IInvitation) => void;
  hideButtons?: boolean;
}

const QUESTIONS = [
  {
    key: 'name',
    label: 'Name',
    placeholder: 'Enter name',
    required: true,
    asterisk: true,
  },
  {
    key: 'relationship',
    label: 'Relationship',
    placeholder: 'Select one',
    required: true,
    type: FieldType.Select,
    asterisk: true,
    options: RELATIONSHIPS_OPTIONS,
  },
  {
    key: 'invitedEmail',
    label: 'Email',
    placeholder: 'Enter email',
    required: true,
    type: 'email',
    asterisk: true,
  },
  {
    key: 'invitedEmailConfirm',
    label: 'Confirm Email',
    placeholder: 'Enter email',
    required: true,
    validator: (_, { invitedEmail }) => (confirmedEmail) => {
      if (!validateEmail(confirmedEmail)) {
        return 'Email is invalid or in use';
      }

      if (invitedEmail !== confirmedEmail) {
        return 'Confirm Email is invalid or does not match';
      }

      return true;
    },
    asterisk: true,
  },
];

const defaultValues = {
  name: '',
  relationship: '',
  invitedEmail: '',
  invitedEmailConfirm: '',
};

const isEmailExistsError = (error: IInvitationError) => {
  const { message, email } = error || ({} as IInvitationError);
  return message === 'User already invited' && !!email;
};

const index = ({
  familyId,
  onSubmit,
  error,
  loading,
  user,
  hideButtons,
}: IProps) => {
  const formRef = useRef(null);

  const emailExistsError = isEmailExistsError(error);

  const handleSubmit = (data) => {
    if (isEqualCaseInsensitive(user.email, data.invitedEmail)) {
      formRef.current.setError('invitedEmail', 'Email is invalid or in use');
      return;
    }

    onSubmit({
      name: data.name.trim(),
      relationship: data.relationship.trim(),
      invitedEmail: data.invitedEmail.trim(),
      familyId,
    });
    formRef.current.reset(defaultValues);
  };

  return (
    <div className={styles.container}>
      {emailExistsError && (
        <Message negative={true} className={styles.message}>
          <Message.Header>
            You already invited user with that email address {error?.email}.
          </Message.Header>
        </Message>
      )}
      <QuashedForm
        ref={formRef}
        defaultValues={defaultValues}
        className={styles.form}
        buttonClassName={styles.btnContainer}
        inputClassName={styles.input}
        questionsClassName={styles.questions}
        questions={QUESTIONS}
        onSubmit={handleSubmit}
        disableSubmit={loading}
        submitText="Share"
        hideButtons={hideButtons}
      />
    </div>
  );
};

export default index;
